import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { Checkbox } from 'components/UI/V3/Forms/Checkbox/Checkbox';
import { ChipSelector, Element } from 'components/Service/ChipSelector';
import { buildSelectedTaxElements, buildTaxElements } from 'components/Service/Pricing/utils/taxElements';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import Service from 'model/Service';
import styles from './DaycareTaxesSelector.module.scss';

interface DaycareTaxesSelectorProps {
    taxable: boolean;
    marketplaces: Service['marketplaces'];
    onTaxableChange: (taxable: boolean) => void;
    onChangeMarketplaces: (marketplaces: Service['marketplaces']) => void;
    className?: string;
}

export const DaycareTaxesSelector: React.FC<DaycareTaxesSelectorProps> = ({
    taxable,
    marketplaces,
    onTaxableChange,
    onChangeMarketplaces,
    className
}) => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const [expandedMarketplaceIds, setExpandedMarketplaceIds] = useState<Array<number>>([]);

    const selectedMarketplaces = useMemo(
        () =>
            marketplaces
                .map(serviceMarketplace => {
                    const servicesInfo = organizationServicesInfo.find(
                        info => info.marketplace.id === serviceMarketplace.marketplaceId
                    );

                    if (!servicesInfo) {
                        return null;
                    }

                    return {
                        serviceMarketplace,
                        servicesInfo
                    };
                })
                .filter(
                    (
                        marketplace
                    ): marketplace is {
                        serviceMarketplace: Service['marketplaces'][number];
                        servicesInfo: (typeof organizationServicesInfo)[number];
                    } => marketplace !== null
                )
                .sort((left, right) =>
                    left.servicesInfo.marketplace.name
                        .toLocaleLowerCase()
                        .localeCompare(right.servicesInfo.marketplace.name.toLocaleLowerCase())
                ),
        [organizationServicesInfo, marketplaces]
    );

    const handleTaxableChange = () => {
        const newTaxable = !taxable;
        onTaxableChange(newTaxable);

        if (!newTaxable) {
            onChangeMarketplaces(
                marketplaces.map(marketplace => ({
                    ...marketplace,
                    taxes: []
                }))
            );
        }
    };

    const marketplaceTaxSelectionChangeHandler =
        (marketplaceId: number) =>
        (elements: Array<Element<number>>): void => {
            onChangeMarketplaces(
                marketplaces.map(marketplace =>
                    marketplace.marketplaceId === marketplaceId
                        ? { ...marketplace, taxes: elements.map(element => element.id) }
                        : marketplace
                )
            );
        };

    const toggleMarketplaceExpanded = (marketplaceId: number) => {
        setExpandedMarketplaceIds(prev =>
            prev.includes(marketplaceId) ? prev.filter(id => id !== marketplaceId) : [...prev, marketplaceId]
        );
    };

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div className={styles.taxableCheckbox}>
                <Checkbox
                    label="Taxable Service"
                    checked={taxable}
                    onChange={handleTaxableChange}
                />
            </div>

            {taxable && (
                <div className={styles.taxesContainer}>
                    <p className={styles.taxesSectionTitle}>Choose the applicable tax for each location</p>
                    {selectedMarketplaces.map(({ serviceMarketplace, servicesInfo }) => {
                        const availableTaxIds = new Set(servicesInfo.taxes.map(tax => tax.id));
                        const selectedCount = serviceMarketplace.taxes.filter(taxId =>
                            availableTaxIds.has(taxId)
                        ).length;
                        const totalCount = servicesInfo.taxes.length;
                        const isExpanded = expandedMarketplaceIds.includes(serviceMarketplace.marketplaceId);

                        return (
                            <div
                                key={serviceMarketplace.marketplaceId}
                                className={styles.marketplaceTaxesSection}
                            >
                                <div
                                    className={styles.marketplaceHeader}
                                    onClick={() => toggleMarketplaceExpanded(serviceMarketplace.marketplaceId)}
                                >
                                    <div className={styles.marketplaceHeaderLeft}>
                                        <p className={styles.marketplaceName}>{servicesInfo.marketplace.name}</p>
                                        <p className={styles.marketplaceCount}>
                                            ({selectedCount}/{totalCount})
                                        </p>
                                    </div>
                                    <FontAwesomeIcon
                                        icon={isExpanded ? faChevronUp : faChevronDown}
                                        className={styles.marketplaceChevron}
                                    />
                                </div>
                                <div className={styles.marketplaceNameDivider} />
                                {isExpanded && (
                                    <div className={styles.marketplaceTaxesContent}>
                                        {servicesInfo.taxes.length > 0 ? (
                                            <ChipSelector
                                                id={`daycare_pricing_taxes_selector_${serviceMarketplace.marketplaceId}`}
                                                searchLabel="Search taxes"
                                                elements={buildTaxElements(servicesInfo.taxes)}
                                                selectedElements={buildSelectedTaxElements(
                                                    servicesInfo.taxes,
                                                    serviceMarketplace.taxes
                                                )}
                                                changeSelection={marketplaceTaxSelectionChangeHandler(
                                                    serviceMarketplace.marketplaceId
                                                )}
                                            />
                                        ) : (
                                            <p className={styles.taxesNotDefined}>Taxes not defined</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
